import countries from 'i18n-iso-countries';
import _concat from 'lodash/concat';
import _defaultTo from 'lodash/defaultTo';
import _delay from 'lodash/delay';
import _divide from 'lodash/divide';
import _find from 'lodash/find';
import _findLast from 'lodash/findLast';
import _flatMapDeep from 'lodash/flatMapDeep';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import _isBoolean from 'lodash/isBoolean';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _isFunction from 'lodash/isFunction';
import _isNil from 'lodash/isNil';
import _isObject from 'lodash/isObject';
import _join from 'lodash/join';
import _keys from 'lodash/keys';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import _orderBy from 'lodash/orderBy';
import _parseInt from 'lodash/parseInt';
import _reduce from 'lodash/reduce';
import _reject from 'lodash/reject';
import _replace from 'lodash/replace';
import _round from 'lodash/round';
import _set from 'lodash/set';
import _size from 'lodash/size';
import _toInteger from 'lodash/toInteger';
import _toLower from 'lodash/toLower';
import _toString from 'lodash/toString';
import _toUpper from 'lodash/toUpper';
import _transform from 'lodash/transform';
import _uniq from 'lodash/uniq';
import _values from 'lodash/values';
import _zipObject from 'lodash/zipObject';
import { parse, format, isValidNumber } from 'libphonenumber-js';
// Color
import { iconPalette, statusPalette } from '../components/core/colors';

// --- //
export const getResourcePeriodDesc = (resource) => {
  let periodDesc = 'noPeriod';
  const { begin, end } = resource;
  if (begin && end) {
    periodDesc = 'beginEnd';
  } else if (begin) {
    periodDesc = 'beginOnly';
  } else if (end) {
    periodDesc = 'endOnly';
  }
  return periodDesc;
};

// --- //
export const buildGraphQLVariables = (data) => ({ input: { ...data } });

// --- //
export const joinNonEmpty = (array, separator = '') => _join(_reject(array, _isEmpty), separator);

// -- //
const listParamsKeys = {
  pageNumberKey: 'page',
  pageSizeKey: 'page_size',
  sortKey: 'sort',
  searchKey: 'search',
};

export const buildListParams = (params, keys = listParamsKeys) => {
  const {
    pagination, sort, filters, extraParams,
  } = params || {};
  const {
    pageNumberKey, pageSizeKey, sortKey, searchKey,
  } = keys || {};
  const objParams = {};
  if (pagination) {
    objParams[pageNumberKey] = pagination.page;
    objParams[pageSizeKey] = pagination.pageSize;
  }
  if (sort) {
    const builtSort = _map(sort, (sortEl) => `${sortEl.field}.${sortEl.dir}`);
    objParams[sortKey] = _join(builtSort, ';');
  }
  if (filters && !_isEmpty(filters)) {
    const builtFilters = _map(
      _reject(filters, ['value', '']),
      (filter) => `${filter.field},${filter.operator}${!_isNil(filter.value) ? `,${filter.value}` : ''}`,
    );
    if (builtFilters && builtFilters.length > 0) {
      objParams[searchKey] = _join(builtFilters, ';');
    }
  }
  if (extraParams && !_isEmpty(extraParams)) {
    _forEach(extraParams, ({ key, value }) => {
      objParams[key] = value;
    });
  }
  return objParams;
};

const graphQLListParamsKeys = {
  paginationKey: {
    rootKey: 'pagination',
    pageNumberKey: 'pageNumber',
    pageSizeKey: 'pageSize',
  },
  sortKey: 'sort',
  searchKey: 'search',
};

// Exemple of filters and plus or
// {
//   "and": [
//     {
//       "or": [
//         {field_1: {operand_1: value_1}},
//         {field_2: {operand_2: value_2}}
//       ]
//     },
//     {
//       "and": [
//          {field_3: {operand_3: value_3}},
//          {field_4: {operand_4: value_4}}
//       ]
//     }
//   ]
// }
export const buildGraphQLListParams = (params, keys = graphQLListParamsKeys) => {
  const { pagination, sort, filters } = params || {};

  const { paginationKey, sortKey, searchKey } = keys || {};
  const objParams = {};
  if (pagination) {
    objParams[paginationKey.rootKey] = {
      [paginationKey.pageNumberKey]: pagination.page,
      [paginationKey.pageSizeKey]: pagination.pageSize,
    };
  }
  if (sort && !_isEmpty(sort)) {
    objParams[sortKey] = _map(sort, ({ field, dir }) => ({ [field]: _toUpper(dir) }));
  }

  if (filters && !_isEmpty(filters)) {
    if (_size(filters) === 1) {
      const { field, operator, value } = filters[0];
      // Only one filter => set as root
      objParams[searchKey] = {
        [field]: { [operator]: value },
      };
    } else {
      // If or is passed add or filter to the query
      const orFilter = _find(filters, 'or');

      if (orFilter && !_isEmpty(orFilter)) {
        objParams[searchKey] = {
          and: [
            {
              and: _map(
                _reject(filters, (filter) => filter.or || filter.value === ''),
                ({ field, operator, value }) => ({ [field]: { [operator]: value } }),
              ),
            },
            {
              or: _map(orFilter.or, ({ field, operator, value }) => ({ [field]: { [operator]: value } })),
            },
          ],
        };
      } else {
        objParams[searchKey] = {
          and: _map(
            _reject(filters, (filter) => filter.or || filter.value === ''),
            ({ field, operator, value }) => ({ [field]: { [operator]: value } }),
          ),
        };
      }
    }
  }
  return objParams;
};

// -- //
export const getCountriesNames = (language) => countries.getNames(language);

// -- //
export const dispatchWithCache = (cached, dispatcher) => {
  if (!cached) {
    dispatcher();
  }
};

// -- //
export const objectValuesToString = (object) => _transform(
  object,
  (result, value, key) => {
    _set(result, key, _toString(value));
    return result;
  },
  {},
);

// --- //
export const getWindowWidthRange = (width) => {
  if (width < 576) {
    return 'xs';
  }
  if (width < 768) {
    return 'sm';
  }
  if (width < 992) {
    return 'md';
  }
  if (width < 1200) {
    return 'lg';
  }
  return 'xl';
};

// --- //
export const buildListSpecs = (searchFields, permanentFiltersSpecs) => {
  if (!_isEmpty(permanentFiltersSpecs)) {
    const permanentFilters = [];
    let excludedFilterFields = [];
    // Parse filters specs
    _forEach(permanentFiltersSpecs, ({ key, value, operator = null }) => {
      // searchFields are defined in each model
      const {
        filter: { field },
        type: { defaultOperator },
        scope,
        excludeFilterFields,
      } = _find(searchFields, ['key', key]);
      permanentFilters.push({ field, operator: operator || defaultOperator(scope), ...(!_isNil(value) && { value }) });
      excludedFilterFields = _concat(excludedFilterFields, excludeFilterFields || []);
    });

    return {
      permanentFilters,
      excludedFilterFields: _uniq(excludedFilterFields),
    };
  }
  return {};
};

// --- //
export const invoke = (func, args) => {
  if (_isFunction(func)) {
    func(args);
  }
};

// --- //
export const readableFileSize = (bytesSize) => {
  if (!bytesSize) {
    return { unit: 'B', value: 0 };
  }
  if (bytesSize < 1024) {
    return { unit: 'B', value: bytesSize };
  }
  if (bytesSize < 1024 * 1024) {
    return { unit: 'KB', value: _round(_divide(bytesSize, 1024), 3) };
  }
  return { unit: 'MB', value: _round(_divide(bytesSize, 1024 * 1024), 3) };
};

// --- //
export const toBooleanOrNull = (value) => {
  if (_isNil(value)) {
    return null;
  }
  if (_isBoolean(value)) {
    return value;
  }
  return _toLower(value) === 'true';
};
export const toIntegerOrNull = (value, setValueTransform = (v) => v) => {
  if (_isNil(value)) {
    return null;
  }
  return _toInteger(setValueTransform(value));
};
export const toLowerOrNull = (value) => {
  if (_isNil(value)) {
    return null;
  }
  return _toLower(value);
};
export const toUpperOrNull = (value) => {
  if (_isNil(value)) {
    return null;
  }
  return _toUpper(value);
};

// --- //
export const sortResourceHistory = (resources, fields = ['end'], dirs = ['desc']) => _orderBy(resources, fields, dirs);

// --- //
export const successWarningIconColor = (success) => (success ? { icon: 'success', color: statusPalette.success } : { icon: 'warning', color: statusPalette.warning });
export const successErrorIconColor = (success) => (success ? { icon: 'success', color: statusPalette.success } : { icon: 'error', color: statusPalette.error });

// --- //
export const addressDesc = (address, separator = ' - ') => (address ? joinNonEmpty([address.street, address.zip_code, address.city, address.country], separator) : '');
export const addressDescCC = (address, separator = ' - ') => (address ? joinNonEmpty([address.street, address.zipCode, address.city, address.country], separator) : '');

/**
 * Formats the address for display on the booking page.
 * @param {object} address - The address object containing street, zip code, city, and country information.
 * @param {string} address.street - The street address.
 * @param {string} address.zipCode - The zip code.
 * @param {string} address.city - The city.
 * @param {string} address.country - The country.
 * @returns {string} - The formatted address string for display on the booking page.
 */
export const formatAddressForCpmClient = (address) => (address ? joinNonEmpty([address.street, joinNonEmpty([address.zipCode, address.city], ' '), address.country], ', ') : '');

// --- //
export const yesNoAsInvertedList = [
  {
    key: 'yes',
    value: false,
    labelId: 'info.yes',
  },
  {
    key: 'no',
    value: true,
    labelId: 'info.no',
  },
];

export const yesNoAsList = [
  {
    key: 'yes',
    value: true,
    labelId: 'info.yes',
  },
  {
    key: 'no',
    value: false,
    labelId: 'info.no',
  },
];

// --- //
export const findInList = (list, listKey, obj, objPath, alt = null) => (obj ? _find(list, [listKey, _get(obj, objPath)]) : alt);
export const findLastInList = (list, listKey, obj, objPath, alt = null) => (obj ? _findLast(list, [listKey, _get(obj, objPath)]) : alt);

// --- //
export const defaultIcon = { icon: 'help', color: iconPalette.base };

// --- //
export const sanitizeInt = (value) => _defaultTo(_parseInt(_replace(value, /[^\d]/g, '')), 0);
export const sanitizeFloat = (value) => _defaultTo(parseFloat(_replace(_replace(value, ',', '.'), /[^\d.-]/g, '')), 0);

export const sanitizeFloatRounded = (precision, allowUndefined = false) => {
  if (allowUndefined) {
    return (value) => ((value && _round(sanitizeFloat(value), 2)) || undefined);
  }
  return (value) => _round(sanitizeFloat(value), precision);
};

// --- //
export const deepValues = (object) => _reduce(
  _values(object),
  (result, value) => {
    if (_isObject(value)) {
      return _concat(result, deepValues(value));
    }
    result.push(value);
    return result;
  },
  [],
);

export const flatMapDeepFromKey = (collection, key) => _flatMapDeep(collection, (c) => {
  const array = [_omit(c, key)];
  if (c[key]) {
    array.push(flatMapDeepFromKey(c[key], key));
  }
  return array;
});

// --- //
export const getHocDisplayName = (hocName, WrappedComponent) => {
  const wcName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  return `${hocName}(${wcName})`;
};

// --- //
export const dispatchResizeEvent = (delay = 0) => _delay(window.dispatchEvent, delay, new Event('resize'));

// --- //
export const objectsToKeys = (objects, key) => _map(objects, (value) => value[key]);

// TODO: to be jsdoc formatted
/**
 * Transforms an array of objects into an object, with 'key' as key for each object
 * example:
 * Transforms [{id: 1, att: 'value1'}, {id: 2, att: 'value2'}] into {1: {id: 1, att: 'value1'}, 2: {id: 2, att: 'value2'}}
 * */
export const arrayToObjects = (array, key) => _zipObject(
  _map(array, (item) => item[key]),
  array,
);

// --- //
export const reduceByFieldAndValue = (collection, field, value) => _reduce(
  collection,
  (result, object) => {
    if (object[field] === value) {
      result.push(object);
    }
    return result;
  },
  [],
);

// --- //
export const formatPhoneNumber = (phone, country) => {
  const parsedPhone = parse(phone); // Returns { country, phone }
  if (!_isEmpty(parsedPhone)) {
    const phoneCountry = parsedPhone.country;
    return format(parsedPhone, phoneCountry === _toUpper(country) ? 'National' : 'International');
  }
  return phone;
};

export const formatPhoneNumberSubmit = (phone) => (_isEmpty(phone) ? '' : format(parse(phone), 'International_plaintext'));

export const isValidPhoneNumber = (phone) => isValidNumber(parse(phone));

// --- //
export const numberTo2DigitsString = (n) => (n > 9 ? _toString(n) : _toString(`0${n}`));

// --- //
export const getDeepKeys = (obj) => {
  let objKeys = [];
  _forEach(_keys(obj), (key) => {
    const value = obj[key];
    if (_isObject(value)) {
      const subkeys = getDeepKeys(value);
      objKeys = _concat(objKeys, subkeys);
    } else {
      objKeys.push(key);
    }
  });
  return objKeys;
};

export const firstLevelDiff = (obj1, obj2) => _reduce(obj1, (result, value, key) => (_isEqual(value, obj2[key]) ? result : _concat(result, key)), []);

// --- //
export const generateId = (ids) => {
  let found = false;
  let id = 1;
  while (!found) {
    if (!_includes(ids, id)) {
      found = true;
    } else {
      id += 1;
    }
  }
  return id;
};

// --- //
export const buildError = (serverError) => (serverError
  ? {
    errors: serverError.data ? serverError.data.errors : null,
    status: serverError.status,
  }
  : null);



// WEBPACK FOOTER //
// ./utils/Tools.js